"use client";

/**
 * React provider + hooks for the active locale.
 *
 * Components consume `useT()` for a bound translator and `useLocale()` for
 * the current locale + a setter. The setter persists to localStorage and
 * triggers a re-render of every consumer.
 *
 * The provider intentionally does not own URL state — pages that want
 * `?lang=…` to be the source of truth read it themselves and pass it as
 * `initialLocale`.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { DEFAULT_LOCALE, type SupportedLocale } from "./config";
import { makeTranslator, type T } from "./translate";
import { resolveClientLocale, writeStoredLocale, readStoredLocale } from "./detect";

type LocaleCtx = {
  locale: SupportedLocale;
  setLocale: (next: SupportedLocale) => void;
  t: T;
};

const Ctx = createContext<LocaleCtx | null>(null);

export function LocaleProvider({
  children,
  initialLocale,
  /** When true, the provider re-resolves the client locale on mount.
   *  Used by the root layout where SSR sees no cookie/storage. */
  resolveOnMount = true,
}: {
  children: React.ReactNode;
  initialLocale?: SupportedLocale;
  resolveOnMount?: boolean;
}) {
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale ?? DEFAULT_LOCALE);

  // Re-resolve on mount so a stored / query / browser preference takes over
  // from the SSR fallback (which can only be the server-side default).
  useEffect(() => {
    if (!resolveOnMount) return;
    const resolved = resolveClientLocale();
    if (resolved !== locale) setLocaleState(resolved);
    // Initial resolution: persist whatever we landed on so the next visit is
    // stable even if the user never opens the switcher.
    if (!readStoredLocale()) writeStoredLocale(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveOnMount]);

  const setLocale = useCallback((next: SupportedLocale) => {
    setLocaleState(next);
    writeStoredLocale(next);
    // Update the document lang attribute so screen readers / browsers pick up
    // the change. Skipped during SSR.
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", next);
    }
  }, []);

  const value = useMemo<LocaleCtx>(() => ({
    locale,
    setLocale,
    t: makeTranslator(locale),
  }), [locale, setLocale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Always returns a valid translator: when there is no provider above, it
 *  falls back to the default locale so non-shell pages (login, public intake)
 *  don't crash if they forget to mount the provider. */
export function useT(): T {
  const ctx = useContext(Ctx);
  return ctx?.t ?? makeTranslator(DEFAULT_LOCALE);
}

export function useLocale(): { locale: SupportedLocale; setLocale: (l: SupportedLocale) => void } {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => { /* no-op when no provider */ },
    };
  }
  return { locale: ctx.locale, setLocale: ctx.setLocale };
}
